import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Drawer,
  Form,
  Input,
  Button,
  Tag,
  Alert,
  message,
  Select,
  Divider,
  Switch,
  Popconfirm,
} from "antd";
import {
  SaveOutlined,
  CloseOutlined,
  PictureOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import customClientAxios from "../customAxios";
import {
  AdminNurtureTemplateBody,
  AdminNurtureTemplateConfig,
  AdminNurtureTemplateHeaderMedia,
} from "../utils/ApiEndpoints";

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 16px 0 8px;
`;

const PreviewCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  font-size: 13px;
  white-space: pre-wrap;
  color: #1e293b;
  margin-bottom: 8px;
  line-height: 1.5;
  .footer { color: #64748b; font-size: 11px; margin-top: 8px; }
`;

const ButtonPreview = styled.div`
  background: #6366f1;
  color: white;
  text-align: center;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
`;

const HeaderImageWrap = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 14px;
  background: #fafbfc;
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 8px;
  img {
    width: 120px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    background: #e2e8f0;
  }
  .empty {
    width: 120px;
    height: 80px;
    border-radius: 8px;
    background: #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 28px;
  }
  .meta {
    flex: 1;
    .name { font-size: 13px; color: #1e293b; font-weight: 600; }
    .hint { font-size: 11px; color: #64748b; margin-top: 4px; }
  }
`;

function extractBody(components) {
  return (components || []).find((c) => c.type === "BODY")?.text || "";
}
function extractButtonText(components) {
  return ((components || []).find((c) => c.type === "BUTTONS")?.buttons || [])[0]?.text || "";
}
function extractFooter(components) {
  return (components || []).find((c) => c.type === "FOOTER")?.text || "";
}

export default function TemplateEditor({ row, sequenceKey, onClose, onSaved }) {
  // Fall back to row.sequenceKey then "prospect" for back-compat with callers
  // that haven't yet passed sequenceKey explicitly.
  const seq = sequenceKey || row?.sequenceKey || "prospect";
  const open = !!row;
  const [bodyForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const tpl = row?.template;
  const status = tpl?.status || "draft";
  const canEditBody = status === "draft" || status === "rejected";
  // Header image: upload allowed for everything except `pending`. For
  // `approved`, uploading flips status back to `draft` (re-approval flow).
  const canEditHeaderImage = status !== "pending";
  const headerMedia = tpl?.headerMedia;

  // Initial values when the drawer opens / row changes
  useEffect(() => {
    if (!row) return;
    bodyForm.setFieldsValue({
      displayName: tpl?.displayName || "",
      category: tpl?.category || "MARKETING",
      body: extractBody(tpl?.components),
      buttonText: extractButtonText(tpl?.components),
      footer: extractFooter(tpl?.components),
    });
    configForm.setFieldsValue({
      isActive: row.isActive,
      landingPathTemplate: row.landingPathTemplate,
      utm_source: row.utmDefaults?.utm_source || "whatsapp",
      utm_medium: row.utmDefaults?.utm_medium || "nurture",
      utm_campaign: row.utmDefaults?.utm_campaign || "",
      utm_content: row.utmDefaults?.utm_content || "",
    });
  }, [row, tpl, bodyForm, configForm]);

  // Live preview state
  const [livePreview, setLivePreview] = useState({
    body: "",
    buttonText: "",
    footer: "",
  });
  useEffect(() => {
    if (!open) return;
    setLivePreview({
      body: extractBody(tpl?.components),
      buttonText: extractButtonText(tpl?.components),
      footer: extractFooter(tpl?.components),
    });
  }, [open, tpl]);

  const handleBodyValuesChange = (_, values) => {
    setLivePreview({
      body: values.body || "",
      buttonText: values.buttonText || "",
      footer: values.footer || "Team Growezy",
    });
  };

  const onPickImage = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    // Client-side guards mirror the server's checks so we fail fast.
    // WhatsApp template headers only accept JPG/PNG — NOT WEBP. msg91 returns
    // "media type is not supported" otherwise.
    const okTypes = ["image/jpeg", "image/png"];
    if (!okTypes.includes(file.type)) {
      message.error("WhatsApp template headers only accept JPG or PNG (not WEBP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Image must be ≤ 5MB (Meta limit).");
      return;
    }

    const form = new FormData();
    form.append("file", file);
    setUploadingImage(true);
    try {
      await customClientAxios().post(
        AdminNurtureTemplateHeaderMedia(row.stepDay, seq),
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      message.success(
        status === "approved"
          ? "Image uploaded — template moved back to draft. Click Submit to request re-approval."
          : "Header image uploaded."
      );
      onSaved?.();
    } catch (e) {
      message.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Upload failed"
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = async () => {
    setUploadingImage(true);
    try {
      await customClientAxios().delete(
        AdminNurtureTemplateHeaderMedia(row.stepDay, seq)
      );
      message.success("Header image removed.");
      onSaved?.();
    } catch (e) {
      message.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Failed to remove image"
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const saveBody = async () => {
    const values = await bodyForm.validateFields();
    setSaving(true);
    try {
      await customClientAxios().patch(AdminNurtureTemplateBody(row.stepDay, seq), {
        displayName: values.displayName,
        category: values.category,
        body: values.body,
        buttonText: values.buttonText,
        footer: values.footer,
      });
      message.success(`${seq} D${row.stepDay} body saved`);
      onSaved?.();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    const values = await configForm.validateFields();
    setSaving(true);
    try {
      await customClientAxios().patch(AdminNurtureTemplateConfig(row.stepDay, seq), {
        isActive: values.isActive,
        landingPathTemplate: values.landingPathTemplate,
        utmDefaults: {
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          utm_content: values.utm_content,
        },
      });
      message.success(`${seq} D${row.stepDay} config saved`);
      onSaved?.();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <div>
          <div style={{ fontWeight: 700 }}>
            Edit Day {row?.stepDay} — {tpl?.displayName}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {tpl?.name}{" "}
            <Tag style={{ marginLeft: 8 }} color={
              status === "approved" ? "success" :
              status === "pending" ? "processing" :
              status === "rejected" ? "error" : "default"
            }>{status}</Tag>
          </div>
        </div>
      }
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={<Button icon={<CloseOutlined />} onClick={onClose} type="text" />}
    >
      {/* ── Header image ────────────────────────────────────────────── */}
      <SectionLabel>Header image (optional)</SectionLabel>
      <HeaderImageWrap>
        {headerMedia?.url ? (
          <img src={headerMedia.url} alt="Header preview" />
        ) : (
          <div className="empty"><PictureOutlined /></div>
        )}
        <div className="meta">
          <div className="name">
            {headerMedia?.url ? "Image attached" : "No image attached"}
          </div>
          <div className="hint">
            {headerMedia?.url
              ? `Type: ${headerMedia.type || "IMAGE"} · Shown above the message on every send.`
              : "JPG or PNG only · max 5MB · 1.91:1 aspect (1200×628) recommended. WEBP is not accepted by WhatsApp."}
          </div>
          {status === "approved" && (
            <div className="hint" style={{ color: "#b45309", marginTop: 6 }}>
              Uploading a {headerMedia?.url ? "new " : ""}image will move this approved template back to draft. You'll need to click <strong>Submit</strong> to request re-approval from Meta.
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadImage(f);
            }}
          />
          <Button
            icon={<UploadOutlined />}
            onClick={onPickImage}
            loading={uploadingImage}
            disabled={!canEditHeaderImage}
            type={headerMedia?.url ? "default" : "primary"}
          >
            {headerMedia?.url ? "Replace" : "Upload image"}
          </Button>
          {headerMedia?.url && (
            <Popconfirm
              title="Remove header image?"
              description={
                status === "approved"
                  ? "This will move the template back to draft. You'll need to submit again."
                  : "The template will revert to text-only."
              }
              onConfirm={removeImage}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                disabled={!canEditHeaderImage || uploadingImage}
              >
                Remove
              </Button>
            </Popconfirm>
          )}
        </div>
      </HeaderImageWrap>
      {!canEditHeaderImage && (
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>
          Image upload is disabled while the template is pending Meta review.
        </div>
      )}

      <Divider />

      {/* ── Body / button ───────────────────────────────────────────── */}
      <SectionLabel>Message body</SectionLabel>
      {!canEditBody && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Body editing is locked — this template is "${status}".`}
          description={
            status === "approved"
              ? "The body and button text can't be changed on an approved template. You can still attach a header image above — doing so moves the template back to draft so you can submit it for a fresh Meta review."
              : status === "pending"
              ? "Wait for Meta to approve or reject, then edit if needed."
              : null
          }
        />
      )}
      {status === "rejected" && tpl?.rejectionReason && (
        <Alert
          type="error"
          message="Meta rejection reason"
          description={tpl.rejectionReason}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}

      <Form
        form={bodyForm}
        layout="vertical"
        onValuesChange={handleBodyValuesChange}
        disabled={!canEditBody || saving}
      >
        <Form.Item name="displayName" label="Display name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Nurture D1 — Welcome" />
        </Form.Item>

        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "MARKETING", label: "Marketing" },
              { value: "UTILITY", label: "Utility" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="body"
          label={
            <span>
              Body text <span style={{ color: "#94a3b8", fontWeight: 400 }}>(use {`{{1}}`} for the prospect's name)</span>
            </span>
          }
          rules={[{ required: true, message: "Body is required" }, { max: 1024, message: "WhatsApp body max 1024 chars" }]}
        >
          <Input.TextArea autoSize={{ minRows: 6, maxRows: 14 }} />
        </Form.Item>

        <Form.Item
          name="buttonText"
          label="CTA button text"
          rules={[{ required: true }, { max: 25, message: "Button max 25 chars" }]}
        >
          <Input placeholder="e.g. Set up my page" />
        </Form.Item>

        <Form.Item name="footer" label="Footer (optional)">
          <Input placeholder="Team Growezy" maxLength={60} />
        </Form.Item>
      </Form>

      <SectionLabel>Preview</SectionLabel>
      <PreviewCard>
        {headerMedia?.url && (
          <img
            src={headerMedia.url}
            alt="Header"
            style={{
              width: "100%",
              maxHeight: 220,
              objectFit: "cover",
              borderRadius: 8,
              marginBottom: 10,
            }}
          />
        )}
        {(livePreview.body || "—").replace("{{1}}", "Priya")}
        {livePreview.footer && <div className="footer">— {livePreview.footer}</div>}
        {livePreview.buttonText && <ButtonPreview>{livePreview.buttonText}</ButtonPreview>}
      </PreviewCard>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>
        Button URL is fixed: <code>https://growezy.club/r/&#123;&#123;1&#125;&#125;</code> — the trailing variable
        is a per-send tracking ID we generate.
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={saveBody}
          loading={saving}
          disabled={!canEditBody}
        >
          Save body
        </Button>
        <div style={{ fontSize: 12, color: "#64748b", alignSelf: "center" }}>
          After saving, go back to the list and click <strong>Submit</strong> to send for Meta approval.
        </div>
      </div>

      <Divider />

      {/* ── Step config (UTM, landing, active) ──────────────────────── */}
      <SectionLabel>Step config</SectionLabel>
      <Form form={configForm} layout="vertical" disabled={saving}>
        <Form.Item name="isActive" label="Active in sequence" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="landingPathTemplate"
          label="Landing page path"
          tooltip="The page the user lands on after clicking the WA button. Appended to https://growezy.club"
          rules={[{ required: true }]}
        >
          <Input placeholder="/lp/welcome" />
        </Form.Item>

        <SectionLabel>UTM defaults</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="utm_source" label="utm_source"><Input placeholder="whatsapp" /></Form.Item>
          <Form.Item name="utm_medium" label="utm_medium"><Input placeholder="nurture" /></Form.Item>
          <Form.Item name="utm_campaign" label="utm_campaign"><Input placeholder={`nurture_d${row?.stepDay}`} /></Form.Item>
          <Form.Item name="utm_content" label="utm_content"><Input placeholder="welcome" /></Form.Item>
        </div>

        <Button type="primary" icon={<SaveOutlined />} onClick={saveConfig} loading={saving}>
          Save config
        </Button>
      </Form>
    </Drawer>
  );
}
